import cx from 'classnames';
import { ChevronDown } from 'lucide-react';
import { DeviceStatusBadge } from '../DeviceStatusBadge';
import type { DeviceListRowProps } from './types';

/**
 * DeviceListRow - Single device row with expansion support
 */
export const DeviceListRow: React.FC<DeviceListRowProps> = ({
  app,
  device,
  expandable,
  isExpanded,
  onToggleExpand,
  onClick,
  children,
  actions,
  t,
}) => {
  const handleClick = () => {
    // When onClick is provided, always use it for row clicks
    // Expansion is handled only by the dedicated arrow button
    if (onClick) {
      onClick(device.id);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Enter on a nested control (Monitor, the expand chevron) bubbles here;
    // claiming it would cancel that control's own click and open the device.
    if (e.target !== e.currentTarget) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick();
    }
  };

  return (
    <div className="border-b border-gray-200 dark:border-gray-700 last:border-b-0">
      {/* Row */}
      <div
        className={cx(
          'grid gap-4 p-4',
          'hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors',
          expandable
            ? 'grid-cols-[3fr_2fr_48px] md:grid-cols-[2fr_1fr_1fr_1fr_48px]'
            : 'grid-cols-[3fr_2fr] md:grid-cols-[2fr_1fr_1fr_1fr]',
          {
            'cursor-pointer': onClick,
            'bg-gray-50 dark:bg-gray-800': isExpanded,
          }
        )}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        role={onClick ? 'button' : undefined}
        tabIndex={onClick ? 0 : undefined}
      >
        {/* Device Name */}
        <div className="flex flex-col justify-center min-w-0">
          <span className="font-semibold text-foreground truncate">
            {device.deviceName}
          </span>
          {(device.field1 || device.field2) && (
            <span className="text-xs text-foreground-secondary md:hidden truncate">
              {device.field1} {device.field1 && device.field2 && '• '}{' '}
              {device.field2}
            </span>
          )}
        </div>

        {/* Serial Number - Desktop only */}
        <div className="hidden md:flex items-center text-sm text-foreground-secondary">
          {device.serialNumber}
        </div>

        {/* Last Active - Desktop only */}
        <div className="hidden md:flex items-center">
          {device.lastSeen && (
            <span
              className="text-sm text-foreground-secondary cursor-help"
              title={device.lastSeenAbsolute}
            >
              {device.lastSeen}
            </span>
          )}
        </div>

        {/* Status + Actions */}
        <div className="flex items-center justify-end gap-2">
          {actions && actions(device)}
          <DeviceStatusBadge app={app} status={device.status} size="sm" t={t} />
        </div>

        {/* Toggle button (if expandable) */}
        {expandable && (
          <div className="flex items-center justify-center">
            <button
              onClick={(e) => {
                e.stopPropagation();
                onToggleExpand();
              }}
              className={cx(
                'p-2 rounded-lg border transition-all',
                'focus:outline-none focus:ring-2 focus:ring-primary-500',
                {
                  'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700':
                    !isExpanded,
                  'border-primary-500 dark:border-primary-400 bg-primary-50 dark:bg-primary-900/20 hover:bg-primary-100 dark:hover:bg-primary-900/30':
                    isExpanded,
                }
              )}
              aria-label={isExpanded ? t('Collapse') : t('Expand')}
              aria-expanded={isExpanded}
              aria-controls={`device-expansion-${device.id}`}
            >
              <ChevronDown
                className={cx('w-5 h-5 transition-transform duration-200', {
                  'text-foreground-secondary': !isExpanded,
                  'text-primary-600 dark:text-primary-400 transform rotate-180':
                    isExpanded,
                })}
              />
            </button>
          </div>
        )}
      </div>

      {/* Expansion Content */}
      {expandable && isExpanded && children && (
        <div
          id={`device-expansion-${device.id}`}
          className="px-4 pb-4 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-200 dark:border-gray-700"
          role="region"
          aria-label={`${device.deviceName} ${t('details')}`}
        >
          <div className="pt-4">{children(device)}</div>
        </div>
      )}
    </div>
  );
};
